import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'
import { Features } from 'generated/featureDefinitions'
import { ShiftManagementRolesRequirementsFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import {
  ShiftType,
  ShiftTypeOptions,
} from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Body/AddUnassignedShiftModal/utils'
import { LocationForm } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Body/LocationForm'
import { StaffRequirements } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Body/StaffRequirements'
import RangeDatePicker from 'modules/shift_management/components/ShiftManagement/RangeDatePicker'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import useLocationIdsFilter from 'modules/shift_management/hooks/useLocationIdsFIlter'
import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'

import NewDefaultShiftForm from './NewDefaultShiftForm'
import NewShiftForm from './NewShiftForm'

type StaffRequirement = {
  job_catalog_roles_ids: number[]
  staff_count: number
}

interface Props {
  unassignedDefaultShiftsForms: UnassignedShiftForm[]
  unassignedShiftForm: UnassignedShiftForm
  onToggleShift: (id: string) => void
  onSelectShiftType: (type: ShiftType) => void
  shiftType: ShiftType
  roles: ShiftManagementRolesRequirementsFragment[]
}

const Content = ({
  unassignedDefaultShiftsForms,
  unassignedShiftForm,
  onToggleShift,
  onSelectShiftType,
  shiftType,
  roles,
}: Props) => {
  const [workAreaFilterValue] = useState(new Field('', 'string'))
  const [{ hasFeatureEntitlement, hasFeatureFlag }] = useEnabledFeatures()
  const isAutomationEnabled = hasFeatureEntitlement(Features.shift_management_automatic_planning)
  const isComplementaryHoursEnabled = hasFeatureFlag('dev_time_tracking_complementary_hours')
  const handleAddStaffRequirement = () => {
    unassignedShiftForm.setValues({
      staff_requirements: [
        ...unassignedShiftForm.get('staff_requirements').value,
        { job_catalog_roles_ids: [], job_catalog_tree_role_uuids: [], staff_count: 1 },
      ],
    })
  }

  const locationIds = useLocationIdsFilter()

  const locationFilterValue =
    locationIds.length === 1 ? new Field(locationIds.at(0), 'string') : new Field('', 'string')

  return (
    <Box flexDirection="column" gap="s24">
      {isAutomationEnabled && (
        <LocationForm
          locationFilterValue={locationFilterValue}
          workAreaFilterValue={workAreaFilterValue}
        />
      )}
      <Box gap="s8">
        <Box flexDirection="row" gap="s4" alignItems="center">
          <Text.Normal size="200" color="grey1000" weight="medium">
            {i18n.t('shift_management.coverage_section.unassigned_shift.date_range')}
          </Text.Normal>
        </Box>
        <RangeDatePicker
          startDate={unassignedShiftForm.get('start_date')}
          endDate={unassignedShiftForm.get('end_date')}
          hideLabel
        />
      </Box>

      <Box gap="s8">
        <Box flexDirection="row" gap="s4" alignItems="center">
          <Text.Normal size="300" color="grey1000" weight="medium">
            {i18n.t('shift_management.coverage_section.unassigned_shift.staff_requirements.title')}
          </Text.Normal>
        </Box>

        {unassignedShiftForm
          .get('staff_requirements')
          .value.map((_: StaffRequirement, i: number) => (
            <StaffRequirements key={i} form={unassignedShiftForm} id={i} roles={roles} />
          ))}
        <Box width="s32" height="s32">
          <Button type="secondary" onClick={handleAddStaffRequirement} iconName="add" />
        </Box>
      </Box>

      <Box gap="s8">
        <NewShiftForm
          form={unassignedShiftForm}
          onSelectNewFromScratch={() => onSelectShiftType(ShiftTypeOptions.custom)}
          isSelected={shiftType === ShiftTypeOptions.custom}
          fromUnassigned
          isAutomationEnabled={isAutomationEnabled}
          locationFilterValue={locationFilterValue.value}
          workAreaFilterValue={workAreaFilterValue.value}
          isComplementaryHoursEnabled={isComplementaryHoursEnabled}
        />

        <NewDefaultShiftForm
          forms={unassignedDefaultShiftsForms}
          onToggleShift={onToggleShift}
          isSelected={shiftType === ShiftTypeOptions.default}
          onSelectDefaultShiftType={() => onSelectShiftType(ShiftTypeOptions.default)}
          isAutomationEnabled={isAutomationEnabled}
          locationFilterValue={locationFilterValue.value}
          workAreaFilterValue={workAreaFilterValue.value}
          isComplementaryHoursEnabled={isComplementaryHoursEnabled}
        />
      </Box>
    </Box>
  )
}

export default observer(Content)
